import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLocalizations } from "@/hooks/useLocalizations";
import { INTERACTION_STATES, TYPOGRAPHY, SPACING } from "@/design-system";

interface UserProfile {
  id: string;
  display_name?: string | null;
  name?: string | null;
  lastname?: string | null;
  business?: string | null;
  image?: string | null;
  about?: string | null;
}

interface UserSelectCardProps {
  expert: UserProfile;
  selected: boolean;
  onToggle: (id: string) => void;
  matchScore?: number;
  matchReason?: string;
}

export function UserSelectCard({ expert, selected, onToggle, matchScore, matchReason }: UserSelectCardProps) {
  const { lCommon } = useLocalizations();
  const userName = expert.display_name || 
    `${expert.name || ''} ${expert.lastname || ''}`.trim() || 
    expert.business || 
    lCommon('labels.expert', { defaultValue: 'Expert' });

  return (
    <div 
      className={cn(
        SPACING.cardPadding,
        'flex items-start gap-3 rounded-lg border cursor-pointer transition-all duration-300 ease-out group overflow-hidden',
        selected 
          ? `${INTERACTION_STATES.card.selected} scale-[1.01]` 
          : `border-border ${INTERACTION_STATES.listItem.hover} hover:scale-[1.02] hover:shadow-md`
      )}
      onClick={() => onToggle(expert.id)}
    >
      <Avatar className="w-10 h-10 flex-shrink-0 overflow-hidden">
        <AvatarImage 
          src={expert.image || undefined} 
          alt={userName} 
          className="transition-transform duration-300 group-hover:scale-110"
        />
        <AvatarFallback className="bg-primary/10 text-primary">
          <User className="w-5 h-5" />
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className={cn(
            TYPOGRAPHY.cardTitleSmall,
            'truncate group-hover:text-primary transition-colors duration-200'
          )}>{userName}</p>
          {matchScore !== undefined && matchScore > 0 && (
            <Badge variant="secondary" className="text-xs shrink-0">
              {matchScore}% match
            </Badge>
          )}
        </div>
        {matchReason ? (
          <p className={cn(TYPOGRAPHY.caption, 'line-clamp-2 mt-0.5 text-primary/80')}>
            {matchReason}
          </p>
        ) : expert.about && (
          <p className={cn(TYPOGRAPHY.caption, 'line-clamp-2 mt-0.5')}>
            {expert.about}
          </p>
        )}
      </div>
      <Checkbox 
        checked={selected}
        onCheckedChange={() => onToggle(expert.id)}
        className="mt-1"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
